/**
 * Streaming DXF parser.
 *
 * Provides drop-in replacements for `tokenizeDxf` and `parseDxfDocument`
 * that avoid materialising the full DXF group list and the full block /
 * entity arrays in memory at once. The motivation is OOM avoidance for
 * large production drawings (200 MB+ files where the eager parser hits
 * 4+ GB peak heap because every transient buffer is a JS array).
 *
 * Public surface (re-exported from ./parser and the package barrel):
 *
 *   - tokenizeDxfStream(source)        → Generator<DxfGroup>
 *   - walkDxfSections(tokenStream)     → Generator<SectionStreamEvent>
 *   - parseDxfDocumentStreaming(src)   → DxfDocument (identical shape to
 *                                         parseDxfDocument, just built
 *                                         via streaming pipeline)
 *
 * Semantics MUST stay identical to the eager parser — the same sections
 * with the same entities in the same order. The only difference is
 * peak heap during the parse call.
 *
 * Strategy:
 *   1. Tokenizer emits {code,value} groups one at a time.
 *   2. A peekable iterator wraps the tokenizer with a single look-ahead
 *      slot so the section walker can inspect the upcoming group
 *      without consuming it.
 *   3. The section walker yields per-section "begin"/"end" events plus
 *      a `body` lazy iterator scoped to the section's content (between
 *      the SECTION/2/<name> header and the matching ENDSEC).
 *   4. ENTITIES and BLOCKS sections drain their body iterator
 *      one entity (or one block) at a time:
 *        - ENTITIES: stream parseSupportedEntity / unsupported-entity
 *          records, applying the INSERT+ATTRIB+SEQEND grouping pass on
 *          the fly so attached attribs are consumed before the next
 *          entity is yielded.
 *        - BLOCKS: stream parseBlockBody for each BLOCK..ENDBLK pair so
 *          one block's child-entity buffer can be GC'd before the next
 *          block starts.
 *   5. TABLES and OBJECTS sections still buffer their full group list
 *      (the eager parsers expect that shape and are O(N) on header
 *      counts which are small). This keeps the change minimal — the
 *      heap wins from streaming come from ENTITIES + BLOCKS which
 *      dominate large files (>95 % of payload).
 */

import {
  type DxfBlockDefinition,
  type DxfBlocksSection,
  type DxfDocument,
  type DxfEntitiesSection,
  type DxfEntity,
  type DxfGroup,
  type DxfSection,
  isSupportedEntityType,
} from "./types";

import { parseSupportedEntity, splitEntities } from "./entityParser";
import {
  foldComplexEntitiesStreaming,
  ownsComplexChildren,
} from "./entityGrouping";
import {
  parseBlockBody,
  parseObjectsSection,
  parseTablesSection,
} from "./sectionParsers";
import { StringPool } from "./stringPool";
import { resolveDxfMLeaderHandles } from "./handleResolution";

/* ──────────────────────────────────────────────────────────────────
 * Tokenizer (generator form)
 * ──────────────────────────────────────────────────────────────── */

/**
 * Returns source.slice(start, end) but as a flat string detached from
 * the parent — V8's String.prototype.slice returns a SlicedString that
 * keeps a reference to the entire parent. For multi-megabyte DXF
 * sources this means every DxfGroup retains the full source, blocking
 * GC and causing OOM in browser workers. We force allocation of a
 * fresh SeqString via String.fromCharCode + chunked apply.
 */
function detachedSlice(source: string, start: number, end: number): string {
  const len = end - start;
  if (len <= 0) return "";
  if (len < 13) {
    // V8 already returns a flat SeqString for slices < 13 chars.
    return source.slice(start, end);
  }
  // Build a flat string via charCode roundtrip in 4096-char chunks.
  // Array.prototype.apply has a stack-limit on argument count; 4096
  // is well within all modern engines (~16k limit).
  const CHUNK = 4096;
  if (len <= CHUNK) {
    const codes = new Array<number>(len);
    for (let k = 0; k < len; k++) codes[k] = source.charCodeAt(start + k);
    return String.fromCharCode.apply(null, codes);
  }
  let result = "";
  for (let j = 0; j < len; j += CHUNK) {
    const chunkEnd = Math.min(j + CHUNK, len);
    const sub = chunkEnd - j;
    const codes = new Array<number>(sub);
    for (let k = 0; k < sub; k++) codes[k] = source.charCodeAt(start + j + k);
    result += String.fromCharCode.apply(null, codes);
  }
  // Force V8 to flatten the ConsString result into a single SeqString
  // so neither parent halves are retained. `.slice(0)` is the canonical
  // V8 flatten idiom — returns the same content but materialised as a
  // fresh SeqString. (Spec text said `.slice(1)` which would drop the
  // first char and silently corrupt long MTEXT contents — interpreted
  // as a typo since the comment clearly intends a flatten.)
  return result.slice(0);
}

/**
 * Generator equivalent of `tokenizeDxf`. Yields {code,value} group
 * pairs one at a time without ever materialising the full array.
 *
 * Identical line-handling semantics to the eager tokenizer — same BOM
 * handling, same trim rules (code lines trimmed, value lines preserved
 * verbatim), same line-terminator logic.
 */
export function* tokenizeDxfStream(source: string): Generator<DxfGroup, void, undefined> {
  const len = source.length;

  // Skip UTF-8 BOM if present at the start.
  let i = 0;
  if (len > 0 && source.charCodeAt(0) === 0xfeff) {
    i = 1;
  }

  let pendingCodeText: string | null = null;
  let pendingCodeLineNumber = 0;
  let lineNumber = 0;

  while (i < len) {
    lineNumber += 1;
    const lineStart = i;
    while (i < len) {
      const ch = source.charCodeAt(i);
      if (ch === 10 /* \n */ || ch === 13 /* \r */) break;
      i += 1;
    }
    const lineEnd = i;
    if (i < len && source.charCodeAt(i) === 13) {
      i += 1;
      if (i < len && source.charCodeAt(i) === 10) i += 1;
    } else if (i < len && source.charCodeAt(i) === 10) {
      i += 1;
    }

    const isLastAndEmpty = lineStart === lineEnd && i === len;
    if (isLastAndEmpty && pendingCodeText === null) {
      lineNumber -= 1;
      continue;
    }

    if (pendingCodeText === null) {
      let codeStart = lineStart;
      let codeEnd = lineEnd;
      while (codeStart < codeEnd) {
        const ch = source.charCodeAt(codeStart);
        if (ch !== 32 && ch !== 9) break;
        codeStart += 1;
      }
      while (codeEnd > codeStart) {
        const ch = source.charCodeAt(codeEnd - 1);
        if (ch !== 32 && ch !== 9) break;
        codeEnd -= 1;
      }
      pendingCodeText = detachedSlice(source, codeStart, codeEnd);
      pendingCodeLineNumber = lineNumber;
    } else {
      const value = detachedSlice(source, lineStart, lineEnd);
      const code = Number.parseInt(pendingCodeText, 10);
      if (!Number.isInteger(code)) {
        throw new Error(
          `Invalid DXF group code "${pendingCodeText}" at line ${pendingCodeLineNumber}`,
        );
      }
      yield { code, value };
      pendingCodeText = null;
    }
  }

  if (pendingCodeText !== null) {
    throw new Error("DXF source must contain an even number of lines");
  }
}

/* ──────────────────────────────────────────────────────────────────
 * Peekable iterator helper
 * ──────────────────────────────────────────────────────────────── */

/**
 * Wraps a generator so callers can peek at the next value without
 * consuming it. We deliberately keep this small (no full unread buffer)
 * because every section parser needs at most one look-ahead slot.
 */
export interface Peekable<T> {
  peek(): T | undefined;
  next(): T | undefined;
  done(): boolean;
}

export function makePeekable<T>(source: Iterator<T>): Peekable<T> {
  let buffered: { value: T } | null = null;
  let exhausted = false;

  function fillBuffer(): void {
    if (buffered !== null || exhausted) return;
    const result = source.next();
    if (result.done) {
      exhausted = true;
      return;
    }
    buffered = { value: result.value };
  }

  return {
    peek(): T | undefined {
      fillBuffer();
      return buffered?.value;
    },
    next(): T | undefined {
      fillBuffer();
      if (buffered === null) return undefined;
      const v = buffered.value;
      buffered = null;
      return v;
    },
    done(): boolean {
      fillBuffer();
      return buffered === null;
    },
  };
}

/* ──────────────────────────────────────────────────────────────────
 * Section walker
 * ──────────────────────────────────────────────────────────────── */

export type SectionStreamEvent = {
  /** The section's name from group code 2 (e.g. "ENTITIES", "TABLES"). */
  name: string;
  /**
   * Yields the inner groups of this section one at a time, stopping
   * when ENDSEC is reached (ENDSEC itself is consumed but not yielded).
   *
   * The caller MUST drain this iterator before reading the next event
   * from the outer walker — otherwise the underlying token stream
   * desyncs.
   */
  body: Iterator<DxfGroup>;
};

/**
 * Walks the top-level DXF group stream and yields one event per
 * SECTION encountered. Stops at the first `0 EOF` marker.
 */
export function* walkDxfSections(
  groups: Iterator<DxfGroup>,
): Generator<SectionStreamEvent, void, undefined> {
  const peekable = makePeekable(groups);

  while (!peekable.done()) {
    const top = peekable.peek();
    if (!top) break;

    if (top.code === 0 && top.value === "EOF") {
      // Consume EOF and stop. Anything after EOF (rare; some legacy
      // emitters add trailing newlines) is silently dropped — same as
      // the eager parser which breaks on EOF.
      peekable.next();
      return;
    }

    if (top.code !== 0 || top.value !== "SECTION") {
      throw new Error(
        `Unexpected top-level group ${top.code}:${top.value}`,
      );
    }
    peekable.next(); // consume SECTION

    const nameGroup = peekable.next();
    if (!nameGroup || nameGroup.code !== 2) {
      throw new Error("SECTION must be followed by a section name group (2)");
    }

    let bodyDone = false;
    const body: Iterator<DxfGroup> = {
      next(): IteratorResult<DxfGroup> {
        if (bodyDone) return { done: true, value: undefined };
        const g = peekable.next();
        if (!g) {
          // Source exhausted before ENDSEC — same fault as eager parser
          // which silently terminated. We keep the streaming parser
          // permissive to match.
          bodyDone = true;
          return { done: true, value: undefined };
        }
        if (g.code === 0 && g.value === "ENDSEC") {
          bodyDone = true;
          return { done: true, value: undefined };
        }
        return { done: false, value: g };
      },
      return(): IteratorResult<DxfGroup> {
        // If a caller stops draining early we still must consume up to
        // ENDSEC so the outer stream stays in sync.
        while (!bodyDone) {
          const g = peekable.next();
          if (!g) {
            bodyDone = true;
            break;
          }
          if (g.code === 0 && g.value === "ENDSEC") {
            bodyDone = true;
            break;
          }
        }
        return { done: true, value: undefined };
      },
    };

    yield { name: nameGroup.value, body };

    // Defensive drain — if the consumer didn't iterate all the way,
    // pump until ENDSEC so the next yield starts at a SECTION header.
    if (!bodyDone) {
      while (!bodyDone) {
        const g = peekable.next();
        if (!g) {
          bodyDone = true;
          break;
        }
        if (g.code === 0 && g.value === "ENDSEC") {
          bodyDone = true;
          break;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────────
 * ENTITIES streaming
 * ──────────────────────────────────────────────────────────────── */

/**
 * Pull entity-shaped chunks out of a section body iterator, one at a
 * time. Each chunk is a list of consecutive groups starting at a
 * `code 0 <type>` header and continuing until the group right before
 * the next `code 0` header (or the end of the body).
 *
 * NOTE: this returns a chunk array per entity but never holds more
 * than one chunk live at once. A separate "first group" buffer is used
 * to remember the header that ended the previous chunk.
 */
function* streamEntityChunks(
  body: Iterator<DxfGroup>,
): Generator<DxfGroup[], void, undefined> {
  let current: DxfGroup[] | null = null;
  while (true) {
    const result = body.next();
    if (result.done) break;
    const g = result.value;
    if (g.code === 0) {
      if (current) {
        yield current;
      }
      current = [g];
      continue;
    }
    if (!current) {
      throw new Error("ENTITIES section contains groups before the first entity header");
    }
    current.push(g);
  }
  if (current) yield current;
}

/**
 * Drain a section body as a stream of fully-parsed DxfEntity records,
 * applying the INSERT+ATTRIB+SEQEND grouping pass so an INSERT that
 * declares attributesFollow swallows the trailing ATTRIB and SEQEND
 * records before the next entity is yielded.
 */
function parseEntityFromGroups(
  entityGroups: DxfGroup[],
  stringPool: StringPool,
): DxfEntity {
  const header = entityGroups[0];
  if (!header || header.code !== 0) {
    throw new Error("Entity record is missing a type header");
  }
  if (isSupportedEntityType(header.value)) {
    return parseSupportedEntity(entityGroups, stringPool);
  }
  return {
    kind: "unsupported-entity",
    type: header.value,
    groups: entityGroups,
  };
}

export function* streamEntitiesUntilEndsec(
  body: Iterator<DxfGroup>,
  stringPool: StringPool,
): Generator<DxfEntity, void, undefined> {
  // We need 1-entity look-ahead because ATTRIB/SEQEND that belong to
  // an INSERT must be consumed during the INSERT's emission. Wrap the
  // entity-chunk generator in a peekable so we can yank attached
  // children without yielding them as siblings.
  const chunkIter = streamEntityChunks(body);
  const peekable = makePeekable(chunkIter);

  while (!peekable.done()) {
    const chunk = peekable.next()!;
    const entity = parseEntityFromGroups(chunk, stringPool);

    // Fold this entity's sibling child run onto it (INSERT+ATTRIB* and
    // POLYLINE+VERTEX*, both terminated by SEQEND) using the SAME shared fold
    // the eager and BLOCKS paths use (./entityGrouping), so the three paths
    // cannot drift apart. The cursor pulls straight off the peekable chunk
    // stream — nothing beyond the one owner's own children is materialised.
    if (entity.kind === "supported-entity" && ownsComplexChildren(entity)) {
      yield foldComplexEntitiesStreaming(entity, {
        done: () => peekable.done(),
        peek: () => {
          const peeked = peekable.peek();
          if (!peeked) return null;
          const peekedHeader = peeked[0];
          if (!peekedHeader || peekedHeader.code !== 0) return null;
          return parseEntityFromGroups(peeked, stringPool);
        },
        advance: () => {
          peekable.next();
        },
      });
      continue;
    }

    yield entity;
  }
}

/* ──────────────────────────────────────────────────────────────────
 * BLOCKS streaming
 * ──────────────────────────────────────────────────────────────── */

/**
 * Drain a BLOCKS section body as a stream of one DxfBlockDefinition
 * per BLOCK/ENDBLK pair. Stray groups outside any BLOCK/ENDBLK record
 * are forwarded via the `rawBlockGroups` callback so the caller can
 * splice them into the assembled DxfBlocksSection.
 */
export function* streamBlocksUntilEndsec(
  body: Iterator<DxfGroup>,
  stringPool: StringPool,
  rawBlockGroupsSink: DxfGroup[],
): Generator<DxfBlockDefinition, void, undefined> {
  const peekable = makePeekable(body);

  // Drain leading non-BLOCK groups.
  while (!peekable.done()) {
    const top = peekable.peek();
    if (!top) break;
    if (top.code === 0 && top.value === "BLOCK") break;
    rawBlockGroupsSink.push(peekable.next()!);
  }

  while (!peekable.done()) {
    const start = peekable.peek();
    if (!start) break;
    if (start.code !== 0 || start.value !== "BLOCK") {
      // Stray group between blocks — preserve it.
      rawBlockGroupsSink.push(peekable.next()!);
      continue;
    }
    peekable.next(); // consume `0 BLOCK`

    // Header groups: until next code 0.
    const headerGroups: DxfGroup[] = [];
    while (!peekable.done()) {
      const g = peekable.peek();
      if (!g) break;
      if (g.code === 0) break;
      headerGroups.push(peekable.next()!);
    }

    // Child groups: until `0 ENDBLK`.
    const childGroups: DxfGroup[] = [];
    while (!peekable.done()) {
      const g = peekable.peek();
      if (!g) break;
      if (g.code === 0 && g.value === "ENDBLK") break;
      childGroups.push(peekable.next()!);
    }

    // ENDBLK + trailing subclass groups: until next code 0 (next BLOCK
    // or end-of-section).
    const endblkGroups: DxfGroup[] = [];
    if (!peekable.done()) {
      const term = peekable.peek();
      if (term && term.code === 0 && term.value === "ENDBLK") {
        endblkGroups.push(peekable.next()!);
        while (!peekable.done()) {
          const g = peekable.peek();
          if (!g) break;
          if (g.code === 0) break;
          endblkGroups.push(peekable.next()!);
        }
      }
    }

    yield parseBlockBody(headerGroups, childGroups, endblkGroups, stringPool);
  }
}

/* ──────────────────────────────────────────────────────────────────
 * Top-level streaming parser
 * ──────────────────────────────────────────────────────────────── */

/**
 * Drop-in replacement for `parseDxfDocument` built on the streaming
 * pipeline. Returns an identical DxfDocument shape to the eager
 * parser — the only difference is intermediate heap usage during
 * the parse call.
 */
export function parseDxfDocumentStreaming(source: string): DxfDocument {
  const stringPool = new StringPool();
  const sections: DxfSection[] = [];

  const tokenIter = tokenizeDxfStream(source);
  const sectionWalker = walkDxfSections(tokenIter);

  for (const ev of sectionWalker) {
    const sectionName = ev.name;

    if (sectionName === "ENTITIES") {
      const entities: DxfEntity[] = [];
      for (const entity of streamEntitiesUntilEndsec(ev.body, stringPool)) {
        entities.push(entity);
      }
      const section: DxfEntitiesSection = {
        kind: "entities-section",
        entities,
      };
      sections.push(section);
      continue;
    }

    if (sectionName === "BLOCKS") {
      const blocks: DxfBlockDefinition[] = [];
      const rawBlockGroups: DxfGroup[] = [];
      for (const block of streamBlocksUntilEndsec(
        ev.body,
        stringPool,
        rawBlockGroups,
      )) {
        blocks.push(block);
      }
      const section: DxfBlocksSection = {
        kind: "blocks-section",
        blocks,
        rawBlockGroups,
      };
      sections.push(section);
      continue;
    }

    // For TABLES / OBJECTS / raw sections we still buffer the section
    // body. These sections are dominated by metadata records which are
    // small relative to ENTITIES and BLOCKS (TABLES is typically
    // < 5 % of total payload, OBJECTS contains MLEADERSTYLE + dynamic
    // block records which are bounded by the block authoring count).
    const sectionGroups = drainBodyToArray(ev.body);

    if (sectionName === "TABLES") {
      sections.push(parseTablesSection(sectionGroups));
    } else if (sectionName === "OBJECTS") {
      sections.push(parseObjectsSection(sectionGroups));
    } else {
      sections.push({
        kind: "raw-section",
        name: sectionName,
        groups: sectionGroups,
      });
    }
  }

  // ── Pass 2: resolve handle-based cross-references ──
  // Shared with parser.ts via resolveDxfMLeaderHandles. MLEADERSTYLE +
  // MLEADER entity text-style (group 340) handle resolution can run only
  // after every section has been visited because the target STYLE / LTYPE
  // / BLOCK entries may live in sections parsed later than the references.
  resolveDxfMLeaderHandles(sections);

  return { sections };
}

/* ──────────────────────────────────────────────────────────────────
 * Internals
 * ──────────────────────────────────────────────────────────────── */

function drainBodyToArray(body: Iterator<DxfGroup>): DxfGroup[] {
  const out: DxfGroup[] = [];
  while (true) {
    const r = body.next();
    if (r.done) break;
    out.push(r.value);
  }
  return out;
}

// Re-export splitEntities so callers that wired against the streaming
// parser have a one-stop shop (matches parser.ts which re-exports it
// for legacy reasons). We do NOT use it in this module — the streamer
// has its own chunker.
export { splitEntities };
